import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import authEn from '@mtm/auth-ui-angular/i18n/en';
import authFr from '@mtm/auth-ui-angular/i18n/fr';

/**
 * `auth.*` keys bundled with the SDK, keyed by language. The SDK ships far more
 * of them than this app mirrors in `assets/i18n/*.json` — the whole
 * `auth.authShell.*` block behind `<mtm-auth>` among them — and it grows every
 * time the SDK gains a screen.
 */
const SDK_TRANSLATIONS: Record<string, Record<string, string>> = {
  en: authEn as Record<string, string>,
  fr: authFr as Record<string, string>,
};

const DEFAULT_LANG = 'en';

/** Replaces `{{name}}` placeholders the SDK bundle uses, the way ngx-translate would. */
function interpolate(value: string, params: Record<string, string>): string {
  return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) => params[name] ?? match);
}

/**
 * Builds the `MTM_TRANSLATE_FN` implementation: app dictionary first, SDK
 * bundle second.
 *
 * The app's own `assets/i18n/*.json` still wins for every key it defines, so
 * existing wording is untouched. Anything it has not mirrored falls back to the
 * SDK's bundled copy instead of rendering as a raw dotted key — which is what
 * the new sign-in shell would otherwise do for all ~68 of its keys.
 *
 * Must be called in an injection context.
 */
export function createMtmTranslateFn(): (key: string, params?: Record<string, string>) => string {
  const translate = inject(TranslateService);

  return (key, params) => {
    const resolved = translate.instant(key, params);
    // ngx-translate echoes the key back when it has no entry for it.
    if (typeof resolved === 'string' && resolved !== key) return resolved;

    const lang = translate.getCurrentLang();
    const fallback = SDK_TRANSLATIONS[lang]?.[key] ?? SDK_TRANSLATIONS[DEFAULT_LANG]?.[key];
    if (fallback === undefined) return key;

    return params ? interpolate(fallback, params) : fallback;
  };
}
